import type { ReactElement } from 'react';

export type IconName =
  | 'dashboard'
  | 'tickets'
  | 'board'
  | 'crm'
  | 'knowledge'
  | 'time'
  | 'reports'
  | 'masterData'
  | 'categories'
  | 'matrix'
  | 'sla'
  | 'customFields'
  | 'snippets'
  | 'templates'
  | 'macros'
  | 'checklists'
  | 'mail'
  | 'automation'
  | 'webForms'
  | 'branding'
  | 'privacy'
  | 'security'
  | 'apiTokens'
  | 'audit'
  | 'backup'
  | 'tokens'
  | 'announcement'
  | 'webhook'
  | 'handbook'
  | 'users'
  | 'roles'
  | 'search'
  | 'command'
  | 'arrowUp'
  | 'arrowDown'
  | 'chevron'
  | 'inbox'
  | 'filter'
  | 'check'
  | 'clock'
  | 'calendar'
  | 'plus'
  // Auszeichnung -- die Werkzeugleiste des Editors (F1-10)
  | 'bold'
  | 'italic'
  | 'underline'
  | 'strike'
  | 'bulletList'
  | 'orderedList'
  | 'quote'
  | 'code'
  | 'link'
  | 'image'
  | 'table'
  | 'undo'
  | 'redo'
  // Beitragsarten und Verlauf
  | 'mailOut'
  | 'mailIn'
  | 'note'
  | 'phone'
  | 'lock'
  | 'system'
  | 'paperclip'
  | 'send'
  | 'upload'
  | 'trash'
  | 'close'
  | 'quoteReply';

/**
 * Die Sinnbilder der Navigation.
 *
 * **Selbst gezeichnet und kein Symbolpaket.** Rund dreissig Umrisse aus
 * geraden Linien und Kreisen rechtfertigen keine Abhaengigkeit (docs/04,
 * Abschnitt 11) -- und ein mitgeliefertes Paket brachte Lizenzbedingungen
 * mit, die in einer Anwendung, die eine Pruefung besteht, jemand lesen
 * muesste. Alles hier entsteht aus Grundformen: Rechteck, Kreis, Linie.
 *
 * **Das Sinnbild steht neben der Beschriftung und nicht statt ihrer.** Ein
 * Zahnrad heisst fuer den einen "Einstellungen" und fuer den anderen
 * "Wartung"; welcher Eintrag gemeint ist, sagt allein das Wort daneben.
 * Deshalb ist jedes Sinnbild ``aria-hidden`` -- ein vorlesendes Programm
 * liest den Text und nicht zweimal dasselbe.
 *
 * Alle Umrisse liegen in einem Raster von 24 und tragen ``currentColor``:
 * Damit folgt die Farbe der Schrift daneben, im hellen wie im dunklen Modus,
 * ohne ein einziges eigenes Token.
 */
export function Icon({ name }: { name: IconName }) {
  return (
    <svg
      className="icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}

const PATHS: Record<IconName, ReactElement> = {
  // Haus -- die Startseite.
  dashboard: (
    <>
      <path d="M3 11 12 3l9 8" />
      <path d="M5 10v10h14V10" />
    </>
  ),
  // Eintrittskarte mit den beiden Einkerbungen.
  tickets: (
    <>
      <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v1a3 3 0 0 0 0 6v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1a3 3 0 0 0 0-6Z" />
      <path d="M14 6v12" strokeDasharray="2 2" />
    </>
  ),
  // Drei Spalten unterschiedlicher Fuellung -- das Board.
  board: (
    <>
      <rect x="3" y="4" width="5" height="16" rx="1" />
      <rect x="9.5" y="4" width="5" height="11" rx="1" />
      <rect x="16" y="4" width="5" height="7" rx="1" />
    </>
  ),
  // Zwei Personen -- Kunden und Ansprechpartner.
  crm: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M16 5.5a3 3 0 0 1 0 5.8" />
      <path d="M17 14.5a6 6 0 0 1 4 5.5" />
    </>
  ),
  // Aufgeschlagenes Buch mit Ruecken.
  knowledge: (
    <>
      <path d="M4 5a2 2 0 0 1 2-2h5v18H6a2 2 0 0 1-2-2Z" />
      <path d="M20 5a2 2 0 0 0-2-2h-5v18h5a2 2 0 0 0 2-2Z" />
    </>
  ),
  // Uhr.
  time: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7v5.5l3.5 2" />
    </>
  ),
  // Saeulen auf einer Grundlinie.
  reports: (
    <>
      <path d="M4 20h16" />
      <path d="M7 20v-6" />
      <path d="M12 20V8" />
      <path d="M17 20v-9" />
    </>
  ),
  // Schieberegler.
  masterData: (
    <>
      <path d="M4 7h16M4 12h16M4 17h16" />
      <circle cx="9" cy="7" r="2" />
      <circle cx="15" cy="12" r="2" />
      <circle cx="8" cy="17" r="2" />
    </>
  ),
  // Ordner mit Reiter.
  categories: (
    <path d="M3 7a2 2 0 0 1 2-2h3.6l2 2H19a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
  ),
  // Vier Felder -- Auswirkung mal Dringlichkeit.
  matrix: (
    <>
      <rect x="3.5" y="3.5" width="7.5" height="7.5" rx="1" />
      <rect x="13" y="3.5" width="7.5" height="7.5" rx="1" />
      <rect x="3.5" y="13" width="7.5" height="7.5" rx="1" />
      <rect x="13" y="13" width="7.5" height="7.5" rx="1" />
    </>
  ),
  // Stoppuhr mit Krone.
  sla: (
    <>
      <circle cx="12" cy="13.5" r="7.5" />
      <path d="M9.5 3h5" />
      <path d="M12 3v3" />
      <path d="M12 10v4h3" />
    </>
  ),
  // Formularzeilen mit Kaestchen davor.
  customFields: (
    <>
      <rect x="3.5" y="5" width="4" height="4" rx="1" />
      <rect x="3.5" y="15" width="4" height="4" rx="1" />
      <path d="M11 7h9.5M11 17h9.5" />
    </>
  ),
  // Zwei Anfuehrungszeichen -- der Textbaustein.
  snippets: (
    <>
      <path d="M5 15c0-4 1.5-6 4.5-7" />
      <path d="M5 15h4v-4H5Z" />
      <path d="M14 15c0-4 1.5-6 4.5-7" />
      <path d="M14 15h4v-4h-4Z" />
    </>
  ),
  // Zwei Blaetter uebereinander -- die Vorlage.
  templates: (
    <>
      <rect x="8" y="3.5" width="12" height="14" rx="2" />
      <path d="M16 20.5H6a2 2 0 0 1-2-2V8" />
    </>
  ),
  // Blitz -- die gebuendelte Aktionsfolge.
  macros: <path d="M13 3 5.5 13.5H12l-1 7.5 7.5-10.5H12Z" />,
  // Kaestchen mit Haken.
  checklists: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="2" />
      <path d="M8 12.5l2.75 2.75L16.5 9.5" />
    </>
  ),
  // Umschlag.
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3.5 7 8.5 6 8.5-6" />
    </>
  ),
  // Zahnrad, auf acht Zacken vereinfacht.
  automation: (
    <>
      <circle cx="12" cy="12" r="3.25" />
      <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.2 5.2l2.1 2.1M16.7 16.7l2.1 2.1M18.8 5.2l-2.1 2.1M7.3 16.7l-2.1 2.1" />
    </>
  ),
  // Fenster mit Eingabezeilen -- das Web-Formular.
  webForms: (
    <>
      <rect x="3.5" y="4" width="17" height="16" rx="2" />
      <path d="M3.5 8.5h17" />
      <path d="M7 12.5h10M7 16h6" />
    </>
  ),
  // Farbpalette.
  branding: (
    <>
      <path d="M12 3.5a8.5 8.5 0 0 0 0 17c1.4 0 2-.9 2-1.8 0-1.4-1-1.7-1-2.7 0-.8.7-1.5 1.6-1.5H17a4 4 0 0 0 4-4c0-3.9-4-7-9-7Z" />
      <circle cx="8" cy="10" r="1.15" />
      <circle cx="12" cy="7.5" r="1.15" />
      <circle cx="16" cy="10" r="1.15" />
    </>
  ),
  // Schild.
  privacy: <path d="M12 3l8 2.8v5.7c0 4.8-3.3 7.9-8 9.5-4.7-1.6-8-4.7-8-9.5V5.8Z" />,
  // Person mit Schluessel -- die Zugaenge.
  users: (
    <>
      <circle cx="10" cy="8" r="3.25" />
      <path d="M3.5 20a6.5 6.5 0 0 1 11.4-4.2" />
      <circle cx="18" cy="17" r="2.5" />
      <path d="M18 19.5V21" />
    </>
  ),
  // Ausweis mit Haken -- eine Rolle ist ein Buendel von Rechten.
  roles: (
    <>
      <rect x="3.5" y="5.5" width="17" height="13" rx="2" />
      <path d="M7 10h5M7 14h3" />
      <path d="m14.5 13 1.8 1.8 3.2-3.6" />
    </>
  ),
  // Geschlossenes Buch mit Lesezeichen -- das Handbuch. Bewusst anders als
  // das aufgeschlagene Buch der Wissensdatenbank: Das eine wird geschrieben,
  // das andere nachgeschlagen.
  handbook: (
    <>
      <path d="M6 3.5h11a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H6a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2Z" />
      <path d="M4 17.5h14" />
      <path d="M9 3.5v6l2-1.5 2 1.5v-6" />
    </>
  ),
  // Schloss.
  security: (
    <>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
      <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" />
    </>
  ),
  // Schluessel -- die Vollmacht, die ein Zugangstoken ist.
  apiTokens: (
    <>
      <circle cx="8" cy="12" r="4" />
      <path d="M12 12h9" />
      <path d="M17 12v3.5" />
      <path d="M20 12v2.5" />
    </>
  ),
  // Lupe -- Suche und Befehlspalette.
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="m20 20-4.5-4.5" />
    </>
  ),
  // Die Schleife der Befehlstaste. Sie steht nur neben der Beschriftung,
  // nie an ihrer Stelle -- auf einer Tastatur ohne diese Taste heisst das
  // Kuerzel anders, und das Wort daneben stimmt in beiden Faellen.
  command: <path d="M8 5a2 2 0 1 0-2 2h12a2 2 0 1 0-2-2v14a2 2 0 1 0 2-2H6a2 2 0 1 0 2 2Z" />,
  // Richtungspfeile fuer den Vergleich an einer Kennzahl. Sie stehen neben
  // dem Vorzeichen und tragen die Aussage nicht allein.
  arrowUp: (
    <>
      <path d="M12 19V5" />
      <path d="m5.5 11.5 6.5-6.5 6.5 6.5" />
    </>
  ),
  // Der Winkel der aufklappbaren Gruppe. Er zeigt nach rechts und dreht sich
  // beim Oeffnen nach unten -- ein Sinnbild statt zweier, und die Drehung
  // sagt dasselbe wie ein Wechsel des Umrisses.
  chevron: <path d="m9.5 5 7 7-7 7" />,

  arrowDown: (
    <>
      <path d="M12 5v14" />
      <path d="m5.5 12.5 6.5 6.5 6.5-6.5" />
    </>
  ),
  // Sprachrohr -- die Ankuendigung an alle zugleich.
  announcement: (
    <>
      <path d="M4 10v4h3l8 4V6l-8 4Z" />
      <path d="M7 14l1.5 5h2.5l-1.2-4.2" />
      <path d="M18.5 9.5a3.5 3.5 0 0 1 0 5" />
    </>
  ),
  // Drei verbundene Knoten -- ein Ereignis geht nach aussen.
  webhook: (
    <>
      <circle cx="12" cy="6" r="2.5" />
      <circle cx="5.5" cy="17" r="2.5" />
      <circle cx="18.5" cy="17" r="2.5" />
      <path d="M10.7 8.2 6.8 14.8M13.3 8.2l3.9 6.6M8 17h8" />
    </>
  ),
  // Ablage -- der Leerzustand einer Liste.
  inbox: (
    <>
      <path d="M4 13V6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5V13" />
      <path d="M4 13h4l1.5 2.5h5L16 13h4v4.5A1.5 1.5 0 0 1 18.5 19h-13A1.5 1.5 0 0 1 4 17.5Z" />
    </>
  ),
  // Trichter -- eine gesetzte Einschraenkung.
  filter: <path d="M4 5h16l-6.5 7.5V19l-3-2v-4.5Z" />,
  // Haken -- erledigt, gesetzt, bestaetigt.
  check: <path d="m5 12.5 4.5 4.5L19 7" />,
  // Zifferblatt -- Zeit und Fristen.
  clock: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  // Kalenderblatt -- Termine (F1-54). Zwei Ringe oben, eine Linie unter
  // dem Kopf: dasselbe Blatt, das jeder Kalender zeigt.
  calendar: (
    <>
      <rect x="4" y="5.5" width="16" height="14" rx="1.5" />
      <path d="M4 10h16" />
      <path d="M8.5 3.5v4" />
      <path d="M15.5 3.5v4" />
    </>
  ),
  // Kreuz -- neu anlegen.
  plus: (
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>
  ),
  // Blatt mit Zeilen und Haken -- das Protokoll: festgehalten und geprueft.
  audit: (
    <>
      <path d="M6 3h9l4 4v14H6Z" />
      <path d="M15 3v4h4" />
      <path d="M9 12h6" />
      <path d="M9 16h4" />
    </>
  ),
  // Zylinder mit Pfeil -- der Datenbestand, der zurueckgeholt werden kann.
  backup: (
    <>
      <ellipse cx="12" cy="6" rx="7" ry="3" />
      <path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6" />
      <path d="M5 12v6c0 1.7 3.1 3 7 3" />
      <path d="m16 17 3 3 3-3" />
    </>
  ),
  // Schluessel -- die hinterlegten Zugaenge.
  tokens: (
    <>
      <circle cx="8" cy="12" r="4" />
      <path d="M12 12h9" />
      <path d="M17 12v3.5" />
      <path d="M20 12v2.5" />
    </>
  ),

  // -- Auszeichnung ------------------------------------------------------
  //
  // Die Werkzeugleiste des Editors. Jedes Zeichen ist die uebliche Form --
  // ein "B" aus zwei Baeuchen, ein geneigter Strich fuer Kursiv --, weil hier
  // die Gewohnheit mehr wiegt als die Eigenstaendigkeit: Wer in einem
  // Textprogramm nach Fett sucht, sucht dieses Zeichen.
  bold: <path d="M7 5h6a3.5 3.5 0 0 1 0 7H7zM7 12h7a3.5 3.5 0 0 1 0 7H7z" strokeWidth="2" />,
  italic: <path d="M15 5h-5M14 5 10 19M14 19H9" />,
  underline: <path d="M7 4v7a5 5 0 0 0 10 0V4M5 20h14" />,
  strike: <path d="M5 12h14M8 5h5a3.5 3.5 0 0 1 1 6.8M10 12.2A3.6 3.6 0 0 0 11 19h5" />,
  bulletList: (
    <>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <circle cx="4.5" cy="6" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="12" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="18" r="1.2" fill="currentColor" stroke="none" />
    </>
  ),
  orderedList: (
    <>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <path d="M4 4.5h1V8M3.6 12.2h2L3.6 15h2M3.6 17h2v3h-2" />
    </>
  ),
  quote: <path d="M7 7h4v4c0 2.5-1.6 4.3-4 5V7ZM14 7h4v4c0 2.5-1.6 4.3-4 5V7Z" />,
  code: <path d="m9 17-5-5 5-5M15 7l5 5-5 5" />,
  link: (
    <>
      <path d="M10.5 13.5a4 4 0 0 0 5.7 0l2.6-2.6a4 4 0 1 0-5.7-5.7l-1 1" />
      <path d="M13.5 10.5a4 4 0 0 0-5.7 0l-2.6 2.6a4 4 0 1 0 5.7 5.7l1-1" />
    </>
  ),
  image: (
    <>
      <rect x="3.5" y="5" width="17" height="14" rx="2" />
      <circle cx="9" cy="10" r="1.5" />
      <path d="m4.5 17 4.5-4.5 3.5 3.5 3-2.5 4.5 4" />
    </>
  ),
  table: (
    <>
      <rect x="3.5" y="5" width="17" height="14" rx="1.5" />
      <path d="M3.5 10h17M9.5 10v9M15 10v9" />
    </>
  ),
  undo: <path d="M9 14 4 9l5-5M4 9h9a7 7 0 0 1 0 14h-2" />,
  redo: <path d="m15 14 5-5-5-5M20 9h-9a7 7 0 0 0 0 14h2" />,

  // -- Beitragsarten -----------------------------------------------------
  //
  // Der Umschlag zeigt in die Richtung, in die die Nachricht laeuft: offen
  // mit sichtbarer Falz nach aussen, geschlossen mit angedeutetem Einwurf
  // nach innen. Das ist der Unterschied, den ein Bearbeiter beim
  // Ueberfliegen des Verlaufs braucht -- wer hat geschrieben, wir oder er.
  mailOut: (
    <>
      <rect x="4" y="6" width="16" height="12" rx="1.5" />
      <path d="m4 7 8 6 8-6" />
    </>
  ),
  mailIn: (
    <>
      <path d="M4 8.5h16V19H4z" />
      <path d="m4 8.5 8-5 8 5" />
      <path d="m4 19 6-5M20 19l-6-5" />
    </>
  ),
  note: (
    <>
      <rect x="4" y="5" width="16" height="14" rx="2" />
      <path d="M8 10h8M8 14h5" />
    </>
  ),
  phone: (
    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a1 1 0 0 1-1 1A15 15 0 0 1 4 5a1 1 0 0 1 1-1Z" />
  ),
  // Das Schloss steht fuer die interne Notiz: Sie verlaesst das Haus nicht.
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="9" rx="1.8" />
      <path d="M8.5 10.5V8a3.5 3.5 0 0 1 7 0v2.5" />
    </>
  ),
  system: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l2.5 2" />
    </>
  ),
  paperclip: (
    <path d="M17 8.5 10 15.5a2.5 2.5 0 0 0 3.5 3.5l7-7a5 5 0 0 0-7-7l-7 7a7.5 7.5 0 0 0 10.5 10.5l5-5" />
  ),
  send: <path d="M4 12 20 4l-7 16-2.5-6.5z" />,
  upload: (
    <>
      <path d="M12 16V5M8 9l4-4 4 4" />
      <path d="M5 16v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2" />
    </>
  ),
  close: <path d="m6 6 12 12M18 6 6 18" />,
  trash: (
    <>
      <path d="M5 7h14M10 7V5h4v2M6.5 7l.8 12.2A1.8 1.8 0 0 0 9.1 21h5.8a1.8 1.8 0 0 0 1.8-1.8L17.5 7" />
    </>
  ),
  // Pfeil, der in ein Zitat zurueckfuehrt -- "diesen Beitrag beantworten".
  quoteReply: (
    <>
      <path d="M10 9 5 13l5 4" />
      <path d="M5 13h8a6 6 0 0 1 6 6v1" />
    </>
  ),
};
